import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useShop } from "../../contexts/ShopContext";
import BenchmarkViewer from "../../components/reportViewers/BenchmarkViewer";
import { getLatestReportByCollection } from "../../utils/api/reportApi";
import styles from "./BrandPrintPage.module.css";

const TAB_LABELS = {
  "brand-assets": "Brand assets",
  "tone-and-voice": "Tone & voice",
};

const BrandPrintPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuthUser();
  const { selectedShop, shopData } = useShop();
  const authToken = user?.token;
  const userId = user?._id || user?.id || user?.userId;
  const shop = selectedShop || shopData || null;
  const tab = TAB_LABELS[searchParams.get("tab")] ? searchParams.get("tab") : "brand-assets";
  const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
  const shopId = isMongoId(shop?._id)
    ? shop._id
    : isMongoId(shop?.id)
      ? shop.id
      : null;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!authToken || !userId || !shopId) return undefined;

    setLoading(true);
    setError(null);

    getLatestReportByCollection({
      collection: "shop_benchmark_report",
      userId,
      shopId,
      authToken,
    })
      .then((response) => {
        if (!active) return;
        setReport(response?.report || null);
      })
      .catch((nextError) => {
        if (!active) return;
        setError(nextError);
        setReport(null);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authToken, shopId, userId]);

  const ready = !loading && !error && Boolean(report?.data);
  const title = useMemo(() => TAB_LABELS[tab] || "Brand assets", [tab]);

  return (
    <main className={styles.page} data-pdf-ready={ready ? "true" : "false"}>
      <section className={styles.sheet}>
        <header className={styles.header}>
          <p className={styles.kicker}>Zeqi Brand Library</p>
          <h1 className={styles.title}>{title}</h1>
          {shop?.name ? <p className={styles.subtitle}>{shop.name}</p> : null}
        </header>

        {loading ? (
          <div className={styles.stateBlock}>Loading benchmark report…</div>
        ) : error ? (
          <div className={styles.stateBlock}>
            {error?.message || "Unable to load benchmark report for export."}
          </div>
        ) : report?.data ? (
          <div className={styles.viewerWrap}>
            <BenchmarkViewer data={report.data} view={tab} />
          </div>
        ) : (
          <div className={styles.stateBlock}>No benchmark report available.</div>
        )}
      </section>
    </main>
  );
};

export default BrandPrintPage;
